import React from 'react';
import { motion } from 'framer-motion';
import { Users, Clock, BellRing, Bot, Rocket, BarChart3, Settings, Megaphone, MessageSquareText, Smile } from 'lucide-react';
const stories = [{
  Icon: Users,
  title: '"We hired Giga"',
  hook: '"We didn\'t buy software last month. We hired someone."',
  desc: "Tell it like a hiring story: what role was open, what Giga took over, what your team stopped doing by hand. The screenshot is the new hire's work.",
  skip: "calling Giga a tool or an assistant. Employees hand you finished work. That's the story."
}, {
  Icon: Clock,
  title: '"Asked at 9:04, done at 9:06"',
  hook: '"Asked Giga for the competitor breakdown at 9:04. Had the finished report at 9:06."',
  desc: 'Name the task, what it used to take, and the exact thing Giga handed back: the report, the PR, the dashboard, the follow-up email. Specific numbers beat adjectives.',
  skip: 'the vague "AI saves me so much time" line. Show the finished work instead.'
}, {
  Icon: BellRing,
  title: '"Giga went first"',
  hook: '"I didn\'t ask for this. Giga flagged it before I knew it was a problem."',
  desc: "Scheduled tasks, monitoring, a heads-up that arrived unprompted. Show the message Giga sent before anyone asked. That's the moment people don't believe until they see it.",
  skip: "cropping out the timestamp or context. Unprompted only lands if it's visibly unprompted."
}, {
  Icon: Bot,
  title: '"Employee > chatbot"',
  hook: '"A chatbot answers questions. An AI employee hands you finished work."',
  desc: 'Take a side. Put your Giga screenshot next to the chat-only workflow it replaced and let the contrast argue for you.',
  skip: "the 5-tools roundup. Multi-tool posts don't qualify, and they don't convert anyway."
}];
const audiences = [{
  Icon: Rocket,
  title: 'Startup founders',
  desc: 'Giga built your internal tool and deployed it in 10 minutes. Screenshot the link.'
}, {
  Icon: BarChart3,
  title: 'Ops & RevOps leads',
  desc: 'Giga audited your ad spend across platforms, found the waste, handed you the PDF.'
}, {
  Icon: Settings,
  title: 'Engineering managers',
  desc: 'Giga read your codebase, opened a PR, and ran the tests. Post the diff.'
}, {
  Icon: Megaphone,
  title: 'Marketing teams',
  desc: 'Giga cross-referenced HubSpot and Google Ads, then built a board-ready report.'
}, {
  Icon: MessageSquareText,
  title: 'Customer success',
  desc: 'Giga summarized 200 support tickets and spotted the pattern your team missed.'
}, {
  Icon: Smile,
  title: 'Anyone, really',
  desc: "If Giga does work for your team in Slack or Microsoft Teams, that's the post."
}];
export function CreatorIdeas() {
  return <div className="space-y-20 md:space-y-28 py-20 md:py-28" data-id="element-2748" data-mp="35yrqj2jtd0nz">
      {/* Four Stories */}
      <section className="px-6 md:px-8 max-w-7xl mx-auto" data-id="element-2749" data-mp="3a0d1gicsf4vj">
        <div className="text-center mb-16" data-id="element-2750" data-mp="1ll999ohgce5">
          <h2 className="font-title font-bold text-4xl md:text-5xl tracking-tight text-[#2B211A] mb-4" data-id="element-2751" data-mp="1kjlq5qa5xfj">
            Four stories that get paid
          </h2>
          <p className="text-neutral-500 text-lg leading-relaxed max-w-3xl mx-auto" data-id="element-2752" data-mp="1r2qa20w7lkyd">
            Any real output qualifies. But the posts that break out tell one of
            four stories. Pick the one that matches your screenshot. The best
            ones get engagement from Giga's own accounts, and more reach means a
            higher tier.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2753" data-mp="184dw81ucrmwg">
          {stories.map((story, i) => {
          const Icon = story.Icon;
          return <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            delay: i * 0.06,
            duration: 0.5
          }} className="relative rounded-[2rem] p-8 md:p-10 bg-white border border-neutral-200 shadow-sm hover:shadow-md transition-shadow flex flex-col group" data-id="element-2754" data-mp="137nihl60t3e7" data-mpi={i}>
                
                <div className="flex items-center gap-4 mb-8" data-id="element-2755" data-mp="y3mqtmb2rudh">
                  <div className="w-14 h-14 rounded-2xl bg-[#FCF3EC] flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform duration-300" data-id="element-2756" data-mp="130d3s0rl29uo">
                    <Icon className="w-7 h-7 text-[#EA580C]" strokeWidth={2} data-id="element-2757" data-mp="1c1teyaq82fzy" />
                  </div>
                  <h3 className="font-title font-bold text-2xl text-[#2B211A]" data-id="element-2758" data-mp="1h0xlsupoc1n4">
                    {story.title}
                  </h3>
                </div>
                <div className="space-y-4 flex-grow" data-id="element-2759" data-mp="1lyfxutyzmwzc">
                  <div className="pl-4 border-l-2 border-neutral-200" data-id="element-2760" data-mp="1qqz8pj6aje0g">
                    <p className="text-[#2B211A] font-medium italic" data-id="element-2761" data-mp="1qt3ek6nr2n3d">
                      {story.hook}
                    </p>
                  </div>
                  <p className="text-neutral-600 leading-relaxed text-[15px]" data-id="element-2762" data-mp="1zsgo0zbvjbo3">
                    {story.desc}
                  </p>
                  <p className="text-neutral-500 leading-relaxed text-[15px]" data-id="element-2763" data-mp="24oe5xa2scq2z">
                    <span className="font-semibold text-neutral-700" data-id="element-2764" data-mp="2ew0a3gh2l51g">
                      Skip:
                    </span>{' '}
                    {story.skip}
                  </p>
                </div>
              </motion.div>;
        })}
        </div>

        <div className="mt-16 text-center max-w-3xl mx-auto" data-id="element-2765" data-mp="20uxmgzaffmof">
          <div className="flex flex-wrap justify-center gap-3 mb-6 text-sm font-medium" data-id="element-2766" data-mp="20wquahws0cy0">
            <span className="text-[#EA580C]" data-id="element-2767" data-mp="2xk596ams81tx">AI employee</span>
            <span className="text-neutral-400 line-through" data-id="element-2768" data-mp="2slk8ka3mbg4y">AI assistant</span>
            <span className="text-neutral-300" data-id="element-2769" data-mp="2nuvimz7l6r7x">·</span>
            <span className="text-[#EA580C]" data-id="element-2770" data-mp="2ntam541poy7f">hired</span>
            <span className="text-neutral-400 line-through" data-id="element-2771" data-mp="2ju1q1sj78o8p">subscribed</span>
            <span className="text-neutral-300" data-id="element-2772" data-mp="3gbejgm4dmumk">·</span>
            <span className="text-[#EA580C]" data-id="element-2773" data-mp="3gczeeculsfky">finished work</span>
            <span className="text-neutral-400 line-through" data-id="element-2774" data-mp="3bk6crxuflnd3">answers</span>
          </div>
          <p className="text-neutral-500 text-[15px] leading-relaxed" data-id="element-2775" data-mp="19nfhem164zv4">
            Use the angle, not our exact words. Near-identical posts get flagged
            in review. Original takes get featured. Screenshots, screen
            recordings, before / afters, and day-in-the-life threads all work
            for every angle.
          </p>
        </div>
      </section>

      {/* Anatomy of a $10,000 post */}
      <section className="px-6 md:px-8 max-w-7xl mx-auto" data-id="element-2776" data-mp="qwemqa72zqhd">
        <div className="text-center mb-16" data-id="element-2777" data-mp="qz0t46i8jzy0">
          <h2 className="font-title font-bold text-4xl md:text-5xl tracking-tight text-[#2B211A] mb-4" data-id="element-2778" data-mp="vwslf1mslo2q">
            Anatomy of a $10,000 post
          </h2>
          <p className="text-neutral-500 text-lg leading-relaxed" data-id="element-2779" data-mp="h0uqtnsiorsm">
            Posts that reach the top cash tiers share the same skeleton. Steal
            it.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start max-w-5xl mx-auto" data-id="element-2780" data-mp="2baoqar68xugb">
          {/* Example Post Card */}
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="bg-white rounded-[2rem] p-8 shadow-xl border border-neutral-200 relative" data-id="element-2781" data-mp="4nzi142uylpp">
            
            <div className="space-y-6 text-[#2B211A] text-[15px] md:text-base leading-relaxed" data-id="element-2782" data-mp="9nc2ji69bgp4">
              <div className="flex gap-4" data-id="element-2783" data-mp="neqmpsbwhnxr">
                <div className="w-6 h-6 rounded-full bg-[#FCF3EC] text-[#EA580C] text-xs font-bold flex items-center justify-center shrink-0 mt-0.5" data-id="element-2784" data-mp="ndpc1oysr3gk">
                  1
                </div>
                <p data-id="element-2785" data-mp="xd6bvprdan4m">Our Monday ad report used to eat half a day.</p>
              </div>
              <div className="flex gap-4" data-id="element-2786" data-mp="sdbq20mg8nqf">
                <div className="w-6 h-6 rounded-full bg-[#FCF3EC] text-[#EA580C] text-xs font-bold flex items-center justify-center shrink-0 mt-0.5" data-id="element-2787" data-mp="semrhtsf9jed">
                  2
                </div>
                <p data-id="element-2788" data-mp="2u6reqryz7sir">
                  Last week I handed it to Giga. It pulled Meta, Google Ads, and
                  Stripe, flagged a campaign quietly burning $1,400 a week, and
                  posted the full breakdown in Slack before my coffee was done.
                </p>
              </div>
              <div className="flex gap-4" data-id="element-2789" data-mp="2i2cbj0k1fv2w">
                <div className="w-6 h-6 rounded-full bg-[#FCF3EC] text-[#EA580C] text-xs font-bold flex items-center justify-center shrink-0 mt-0.5" data-id="element-2790" data-mp="2n0xayxrguaqa">
                  3
                </div>
                <p data-id="element-2791" data-mp="1uc9oiyuzdfb5">We didn't add another tool. We hired an AI employee.</p>
              </div>
              <div className="flex gap-4" data-id="element-2792" data-mp="1ue2uebxrrvi3">
                <div className="w-6 h-6 rounded-full bg-[#FCF3EC] text-[#EA580C] text-xs font-bold flex items-center justify-center shrink-0 mt-0.5" data-id="element-2793" data-mp="1zab59d4xp2jg">
                  4
                </div>
                <p data-id="element-2794" data-mp="3arauu0zwd39s">Output below. 👇</p>
              </div>
              <div className="flex gap-4" data-id="element-2795" data-mp="3asuc9uxahkd5">
                <div className="w-6 h-6 rounded-full bg-[#FCF3EC] text-[#EA580C] text-xs font-bold flex items-center justify-center shrink-0 mt-0.5" data-id="element-2796" data-mp="3fot5pwxh5skf">
                  5
                </div>
                <p className="text-[#EA580C] font-medium" data-id="element-2797" data-mp="z2w9yttn18sz">#ad @askgiga</p>
              </div>
            </div>
          </motion.div>

          {/* Steps */}
          <div className="space-y-6" data-id="element-2798" data-mp="ua36085zu4hy">
            {[{
            title: 'Open with a cost',
            desc: 'A chore your audience recognizes. Stakes first, product second.'
          }, {
            title: 'Specific ask, specific result',
            desc: 'Numbers, tool names, real output. This is the line people quote.'
          }, {
            title: 'One category line',
            desc: "Employee, not tool. It's the take people argue with in the comments, and comments are reach."
          }, {
            title: 'The screenshot is the proof',
            desc: 'The story is the post, the output is the evidence. Never post the screenshot alone.'
          }, {
            title: 'Disclosure + tag',
            desc: "Without these the post can't be approved or paid. Every platform, every post."
          }].map((step, i) => <motion.div key={i} initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="flex gap-4" data-id="element-2799" data-mp="2biawrupo720e" data-mpi={i}>
              
                <div className="w-6 h-6 rounded-full bg-[#FCF3EC] text-[#EA580C] text-xs font-bold flex items-center justify-center shrink-0 mt-0.5" data-id="element-2800" data-mp="1xrftcxxxxd47">
                  {i + 1}
                </div>
                <div data-id="element-2801" data-mp="26iybheeg62zx">
                  <h4 className="font-bold text-[#2B211A] mb-1" data-id="element-2802" data-mp="26k9br48oosma">
                    {step.title}
                  </h4>
                  <p className="text-neutral-500 text-sm leading-relaxed" data-id="element-2803" data-mp="21pvdmu2n07dx">
                    {step.desc}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="px-6 md:px-8 max-w-7xl mx-auto" data-id="element-2804" data-mp="zwlhk2yr9d0j">
        <div className="bg-[#2B211A] rounded-[2.5rem] md:rounded-[3rem] p-8 md:p-16 lg:p-20 relative overflow-hidden shadow-xl" data-id="element-2805" data-mp="zv0nsfk9ly3q">
          <div className="absolute inset-0 dark-grid opacity-50" data-id="element-2806" data-mp="pz7ct0m72co2" />
          <div className="text-center mb-14 md:mb-16 relative z-10" data-id="element-2807" data-mp="pwup06tr9833">
            <h2 className="font-title font-bold text-4xl md:text-5xl tracking-tight text-white mb-4" data-id="element-2808" data-mp="uya7br4jt0hs">
              Who this is for
            </h2>
            <p className="text-white/80 text-lg" data-id="element-2809" data-mp="1nm63muq7gqe1">
              If Giga does work for your team, you qualify.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 relative z-10" data-id="element-2810" data-mp="18odssfvlsq66">
            {audiences.map((a, i) => {
            const Icon = a.Icon;
            return <motion.div key={i} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-50px'
            }} transition={{
              delay: i * 0.07,
              duration: 0.5
            }} className="rounded-[2rem] p-8 md:p-10 text-center bg-white/5 border border-white/10 hover:bg-white/10 transition-colors" data-id="element-2811" data-mp="3tf4q3ifnk0qm" data-mpi={i}>
                  
                  <div className="flex justify-center mb-6" data-id="element-2812" data-mp="1q7nnu1pgbh2l">
                    <div className="w-16 h-16 rounded-2xl bg-white/10 flex items-center justify-center" data-id="element-2813" data-mp="1q51ebwj02rhq">
                      <Icon className="w-8 h-8 text-white" strokeWidth={2} data-id="element-2814" data-mp="1h67apm7950uo" />
                    </div>
                  </div>
                  <h3 className="font-title font-bold text-xl md:text-2xl mb-3 text-white" data-id="element-2815" data-mp="1c6l1dnujo4r5">
                    {a.title}
                  </h3>
                  <p className="text-white/70 leading-relaxed text-[15px] max-w-xs mx-auto" data-id="element-2816" data-mp="179uhsqjj6r0z">
                    {a.desc}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>
      </section>
    </div>;
}